import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { i as isRequired, a as assignDescription, m as messageId } from './form.js';
import { d as debounceEvent } from './utils.js';
import { i as inheritAriaAttributes, a as inheritAttributes } from './attributes.js';
import { b as danger } from './index2.js';
import { d as defineCustomElement$2 } from './pds-icon2.js';

const labelCss = ":host{display:inline-block}:host([aria-disabled=true]) label{color:var(--pine-color-text-label-disabled)}:host([aria-readonly=true]) label{color:var(--pine-color-text-label-readonly)}label{color:var(--pine-color-text-label);font:var(--pine-typography-body-medium);letter-spacing:var(--pine-letter-spacing)}";
const PdsTextareaStyle0 = labelCss;

const pdsInputTokensCss = ":host{--pine-input-color-background-danger:var(--pine-color-red-050)}";
const PdsTextareaStyle1 = pdsInputTokensCss;

const pdsTextareaCss = ":host{--box-shadow-focus:0 0 0 1px #ffffff, 0 0 0 3px var(--pine-color-focus-ring);--box-shadow-focus-error:0 0 0 1px #ffffff, 0 0 0 3px var(--pine-color-focus-ring-danger);display:inline}:host([aria-readonly=true]) textarea{background-color:var(--pine-color-background-container-disabled);color:var(--pine-color-text-readonly)}.pds-textarea{display:-ms-flexbox;display:flex;-ms-flex-direction:column;flex-direction:column}label{display:block;-webkit-margin-after:var(--pine-dimension-xs);margin-block-end:var(--pine-dimension-xs)}.pds-textarea__field{background-color:var(--pine-color-background-container);border:var(--pine-border);border-radius:calc(var(--pine-dimension-xs) * 1.25);font:var(--pine-typography-body);letter-spacing:var(--pine-letter-spacing);padding:var(--pine-dimension-xs) var(--pine-dimension-sm)}.pds-textarea__field:hover:not(:disabled,.is-invalid){border:var(--pine-border-hover)}.pds-textarea__field:disabled{background-color:var(--pine-color-background-container-disabled);color:var(--pine-color-text-disabled);cursor:not-allowed}.pds-textarea__field:focus-visible{-webkit-box-shadow:var(--box-shadow-focus);box-shadow:var(--box-shadow-focus);outline:none}.pds-textarea__field::-webkit-input-placeholder{color:var(--pine-color-text-placeholder)}.pds-textarea__field::-moz-placeholder{color:var(--pine-color-text-placeholder)}.pds-textarea__field:-ms-input-placeholder{color:var(--pine-color-text-placeholder)}.pds-textarea__field::-ms-input-placeholder{color:var(--pine-color-text-placeholder)}.pds-textarea__field::placeholder{color:var(--pine-color-text-placeholder)}.pds-textarea__field.is-invalid{background-color:var(--pine-input-color-background-danger);border-color:var(--pine-color-border-danger)}.pds-textarea__field.is-invalid:focus-visible{-webkit-box-shadow:var(--box-shadow-focus-error);box-shadow:var(--box-shadow-focus-error)}.pds-textarea__error-message,.pds-textarea__helper-message{font:var(--pine-typography-body-sm-medium);-webkit-margin-before:var(--pine-dimension-xs);margin-block-start:var(--pine-dimension-xs)}.pds-textarea__error-message{-ms-flex-align:center;align-items:center;color:var(--pine-color-text-message-danger);display:-ms-flexbox;display:flex;gap:var(--pine-dimension-2xs)}";
const PdsTextareaStyle2 = pdsTextareaCss;

const PdsTextarea$1 = /*@__PURE__*/ proxyCustomElement(class PdsTextarea extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.pdsBlur = createEvent(this, "pdsBlur", 7);
        this.pdsFocus = createEvent(this, "pdsFocus", 7);
        this.pdsInput = createEvent(this, "pdsInput", 7);
        this.pdsTextareaChange = createEvent(this, "pdsTextareaChange", 7);
        this.inheritedAttributes = {};
        this.onBlur = (ev) => {
            this.hasFocus = false;
            if (this.focusedValue !== this.value) {
                this.emitValueChange(ev);
            }
            this.pdsBlur.emit(ev);
        };
        this.onFocus = (ev) => {
            this.hasFocus = true;
            this.focusedValue = this.value;
            this.pdsFocus.emit(ev);
        };
        this.onInput = (ev) => {
            const input = ev.target;
            if (input) {
                this.value = input.value || '';
            }
            this.emitInputChange(ev);
        };
        this.onTextareaChange = (ev) => {
            this.emitValueChange(ev);
        };
        this.autocomplete = undefined;
        this.componentId = undefined;
        this.disabled = false;
        this.debounce = undefined;
        this.errorMessage = undefined;
        this.helperMessage = undefined;
        this.invalid = false;
        this.label = undefined;
        this.name = this.componentId;
        this.placeholder = undefined;
        this.readonly = false;
        this.required = false;
        this.rows = undefined;
        this.value = '';
        this.hasFocus = false;
    }
    /**
     * Sets focus on the native `textarea` in the `pds-texarea`. Use this method instead of the global
     * `textarea.focus()`.
     */
    async setFocus() {
        if (this.nativeTextarea) {
            this.nativeTextarea.focus();
        }
    }
    debounceChanged() {
        const { pdsInput, debounce, originalPdsInput } = this;
        this.pdsInput = debounce === undefined ? originalPdsInput !== null && originalPdsInput !== void 0 ? originalPdsInput : pdsInput : debounceEvent(pdsInput, debounce);
    }
    /**
     * Update the native input element when the value changes
     */
    valueChanged() {
        const nativeTextarea = this.nativeTextarea;
        const value = this.getValue();
        if (nativeTextarea && nativeTextarea.value !== value) {
            nativeTextarea.value = value;
        }
    }
    /**
     * Emits an `pdsInput` event.
     */
    emitInputChange(event) {
        const { value } = this;
        this.pdsInput.emit({ value, event });
    }
    /**
     * Emits an `pdsTextareaChange` event.
     */
    emitValueChange(event) {
        const textarea = event.target;
        isRequired(textarea, this);
        const { value } = textarea;
        // Checks for both null and undefined values
        const newValue = value == null ? value : value.toString();
        this.focusedValue = newValue;
        this.pdsTextareaChange.emit({ value: newValue, event });
    }
    getValue() {
        return this.value || '';
    }
    textareaClassNames() {
        const classNames = ['pds-textarea__field'];
        if (this.invalid && this.invalid === true) {
            classNames.push('is-invalid');
        }
        return classNames.join('  ');
    }
    connectedCallback() {
        this.debounceChanged();
    }
    componentWillLoad() {
        this.inheritedAttributes = Object.assign(Object.assign({}, inheritAriaAttributes(this.el)), inheritAttributes(this.el));
    }
    componentDidLoad() {
        this.originalPdsInput = this.pdsInput;
    }
    render() {
        const value = this.getValue();
        return (h(Host, { key: 'aa960b2a67c927c296040e835fadcd8f8bcbbc18', "aria-disabled": this.disabled ? 'true' : null, "aria-readonly": this.readonly ? 'true' : null }, h("div", { key: 'b95804e8ddbd92f4d7177c90d342d0ff2022ad44', class: "pds-textarea" }, this.label &&
            h("label", { key: 'ef508b6af03cf871b419b2dd4d79ded182b59da5', htmlFor: this.componentId }, this.label), h("textarea", Object.assign({ key: '2d2f195e8a715332eab0bba4f869db67b0f249c9', ref: (el) => this.nativeTextarea = el, "aria-describedby": assignDescription(this.componentId, this.invalid, this.helperMessage), "aria-invalid": this.invalid ? "true" : undefined, autocomplete: this.autocomplete, class: this.textareaClassNames(), disabled: this.disabled, id: this.componentId, name: this.name, placeholder: this.placeholder, readOnly: this.readonly, required: this.required, rows: this.rows, onBlur: this.onBlur, onChange: this.onTextareaChange, onFocus: this.onFocus, onInput: this.onInput }, this.inheritedAttributes), value), this.helperMessage &&
            h("p", { key: '84ad453017c378553eeda5198267564661b9bc8f', class: "pds-textarea__helper-message", id: messageId(this.componentId, 'helper') }, this.helperMessage), this.invalid &&
            h("p", { key: 'd1fbcbe5af314a25bdec647d560f9e964a9c8b90', "aria-live": "assertive", class: "pds-textarea__error-message", id: messageId(this.componentId, 'error') }, h("pds-icon", { key: '525edfdafeabd31a41e62c0c0e454752123c7c8a', icon: danger, size: "small" }), this.errorMessage))));
    }
    get el() { return this; }
    static get watchers() { return {
        "debounce": ["debounceChanged"],
        "value": ["valueChanged"]
    }; }
    static get style() { return PdsTextareaStyle0 + (PdsTextareaStyle1 + PdsTextareaStyle2); }
}, [1, "pds-textarea", {
        "autocomplete": [1],
        "componentId": [1, "component-id"],
        "disabled": [4],
        "debounce": [2],
        "errorMessage": [1, "error-message"],
        "helperMessage": [1, "helper-message"],
        "invalid": [1028],
        "label": [1],
        "name": [1],
        "placeholder": [1],
        "readonly": [4],
        "required": [4],
        "rows": [2],
        "value": [1025],
        "hasFocus": [32],
        "setFocus": [64]
    }, undefined, {
        "debounce": ["debounceChanged"],
        "value": ["valueChanged"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["pds-textarea", "pds-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "pds-textarea":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, PdsTextarea$1);
            }
            break;
        case "pds-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const PdsTextarea = PdsTextarea$1;
const defineCustomElement = defineCustomElement$1;

export { PdsTextarea, defineCustomElement };

//# sourceMappingURL=pds-textarea.js.map